import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { api } from '@/lib/api/endpoints';

const SESSION_KEY = 'if_analytics_session_id';

function ensureSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const next = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, next);
    return next;
  } catch {
    return crypto.randomUUID();
  }
}

/**
 * Records session_start once per tab and page_view on route changes.
 * Failures are swallowed — analytics must never break the shell.
 */
export function useAnalyticsTracker(enabled: boolean): void {
  const location = useLocation();
  const startedRef = useRef(false);
  const lastPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const sessionId = ensureSessionId();
    const path = location.pathname || '/';

    if (!startedRef.current) {
      startedRef.current = true;
      void api.me
        .trackAnalytics({
          session_id: sessionId,
          event_name: 'session_start',
          path,
        })
        .catch(() => {
          /* ignore */
        });
    }

    if (lastPathRef.current === path) return;
    lastPathRef.current = path;

    void api.me
      .trackAnalytics({
        session_id: sessionId,
        event_name: 'page_view',
        path,
      })
      .catch(() => {
        /* ignore */
      });
  }, [enabled, location.pathname]);
}
